import React, { useEffect, useRef } from "react";
import { Layout } from "../components/Layout";
import TextTransition, { presets } from "react-text-transition";

/* ─── Animated heading ─── */
export const AnimatedText = () => {
  const [index, setIndex] = React.useState(0);
  const TEXTS = ["Our Mission", "Our Vision", "Our Value"];
  React.useEffect(() => {
    const id = setInterval(() => setIndex((i) => i + 1), 3000);
    return () => clearTimeout(id);
  }, []);
  return (
    <TextTransition text={TEXTS[index % TEXTS.length]} springConfig={presets.gentle} />
  );
};

/* ─── Section reveal hook ─── */
function useReveal(threshold = 0.15) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    const el = ref.current;
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          el.style.opacity = "1";
          el.style.transform = "translateY(0)";
          obs.disconnect();
        }
      },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return ref;
}

/* ─── Value Card ─── */
const ValueCard = ({ icon, title, gradient, items, text, delay = "0s" }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    const el = ref.current;
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          el.style.opacity = "1";
          el.style.transform = "translateY(0)";
          obs.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      style={{
        opacity: 0,
        transform: "translateY(40px)",
        transition: `opacity 0.7s ease ${delay}, transform 0.7s ease ${delay}`,
        background: "#fff",
        borderRadius: 20,
        padding: "2.5rem 2rem",
        boxShadow: "0 15px 50px rgba(0,0,0,0.09)",
        border: "1px solid rgba(0,184,184,0.1)",
        position: "relative",
        overflow: "hidden",
        height: "100%",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-8px)";
        e.currentTarget.style.boxShadow = "0 25px 70px rgba(0,0,0,0.15)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = "0 15px 50px rgba(0,0,0,0.09)";
      }}
    >
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 4, background: gradient, borderRadius: "20px 20px 0 0" }} />
      <div style={{ width: 60, height: 60, borderRadius: 16, background: "rgba(0,184,184,0.12)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "1.5rem", fontSize: "1.5rem", color: "#00b8b8" }}>
        <i className={icon} />
      </div>
      <h4 style={{ fontWeight: 700, color: "#1a2332", marginBottom: "1rem", fontSize: "1.1rem" }}>{title}</h4>
      {text && <p style={{ color: "#6b7280", lineHeight: 1.8, fontSize: "0.9rem", margin: 0 }}>{text}</p>}
      {items && (
        <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
          {items.map((item, i) => (
            <li key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "0.4rem 0", color: "#374151", fontSize: "0.9rem" }}>
              <span style={{ width: 22, height: 22, borderRadius: "50%", background: gradient, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: "0.6rem", color: "#fff" }}>
                <i className="fas fa-check" />
              </span>
              {item}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export const About2ndOpinion = () => {
  const heroRef = useReveal();
  const storyRef = useReveal();

  const missionItems = [
    "Breaking down geographic barriers",
    "Offering flexible, convenient options",
    "Ensuring access for underserved populations",
    "Safeguarding privacy and clinical safety",
  ];

  return (
    <Layout
      subtitle={"  "}
      title={"Our Mission • Our Vision • Our Value"}
      bgimage={require("../media/images/Group27.png").default}
      jumpto="about"
      _renderTitle={AnimatedText}
    >
      {/* ── Hero intro ── */}
      <section
        id="about"
        style={{
          padding: "6rem 0",
          background: "linear-gradient(180deg,#f0f9f9 0%,#fff 100%)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div style={{ position: "absolute", width: 500, height: 500, background: "rgba(0,184,184,0.08)", borderRadius: "50%", filter: "blur(80px)", top: -150, right: -150, pointerEvents: "none" }} />
        <div style={{ position: "absolute", width: 350, height: 350, background: "rgba(55,81,75,0.07)", borderRadius: "50%", filter: "blur(60px)", bottom: -80, left: -100, pointerEvents: "none" }} />

        <div className="container" style={{ position: "relative", zIndex: 1 }}>
          <div className="row align-items-center g-5">
            {/* Image */}
            <div className="col-12 col-lg-5">
              <div
                ref={heroRef}
                style={{
                  opacity: 0,
                  transform: "translateY(40px)",
                  transition: "opacity 0.8s ease, transform 0.8s ease",
                  position: "relative",
                }}
              >
                <div style={{ position: "absolute", inset: -30, background: "radial-gradient(ellipse,rgba(0,184,184,0.2) 0%,transparent 70%)", borderRadius: 30, zIndex: 0 }} />
                <div style={{ borderRadius: 24, overflow: "hidden", boxShadow: "0 30px 80px rgba(0,0,0,0.15)", position: "relative", zIndex: 1 }}>
                  <img
                    src={require("../media/images/about-rural-suburban-urban-healthcare.png").default}
                    alt="Healthcare access across rural, suburban and urban communities"
                    style={{ width: "100%", display: "block" }}
                  />
                </div>
                <div
                  style={{
                    position: "absolute",
                    bottom: -20,
                    right: -20,
                    background: "linear-gradient(135deg,#37514B 0%,#00b8b8 100%)",
                    borderRadius: 20,
                    padding: "1.25rem 1.5rem",
                    boxShadow: "0 10px 30px rgba(0,184,184,0.4)",
                    color: "#fff",
                    zIndex: 2,
                    animation: "floatSlow 5s ease-in-out infinite",
                  }}
                >
                  <div style={{ fontWeight: 800, fontSize: "1.5rem", lineHeight: 1 }}>10K+</div>
                  <div style={{ fontSize: "0.75rem", opacity: 0.85 }}>Patients Served</div>
                </div>
              </div>
            </div>

            {/* Text */}
            <div className="col-12 col-lg-6 offset-lg-1">
              <div
                ref={storyRef}
                style={{
                  opacity: 0,
                  transform: "translateY(40px)",
                  transition: "opacity 0.8s ease 0.2s, transform 0.8s ease 0.2s",
                }}
              >
                <div className="section-tag">Who We Are</div>
                <div className="accent-line" style={{ margin: "1rem 0 1.5rem" }} />
                <h2 className="section-title-modern" style={{ marginBottom: "1.25rem" }}>
                  Bridging the Healthcare Gap in <span>Rural, Suburban and Urban Areas</span>
                </h2>
                <p style={{ color: "#4b5563", lineHeight: 1.8, fontSize: "1rem", marginBottom: "1.25rem" }}>
                  Healthcare workforce shortage impacts healthcare supply and access in rural, suburban and urban communities.
                  With strategic partnerships and efficient use of technology, 24Telemed strives to bridge
                  service gaps and limit barriers to care — including workforce shortages, health insurance
                  status, transportation issues, and health literacy.
                </p>
                <p style={{ color: "#4b5563", lineHeight: 1.8, fontSize: "1rem", marginBottom: "2rem" }}>
                  We design predictable and accessible telehealth solutions, ranging from telephonic to
                  virtual consultation services, filling gaps in healthcare service delivery across Nigeria.
                </p>
                <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
                  <a href="/contact" className="btn-modern-primary">
                    <span>Contact Us</span>
                    <i className="fas fa-arrow-right" />
                  </a>
                  <a href="/solutions" className="btn-modern-outline">
                    <span>Our Services</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Mission / Vision / Values ── */}
      <section style={{ padding: "5rem 0 6rem", background: "#fff", position: "relative" }}>
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 4, background: "linear-gradient(90deg,#37514B,#00b8b8,#37514B)", backgroundSize: "200% auto", animation: "borderFlow 3s linear infinite" }} />
        <div className="container">
          <div className="row justify-content-center" style={{ marginBottom: "3.5rem" }}>
            <div className="col-12 col-lg-7 text-center">
              <div className="section-tag">Our Foundation</div>
              <div className="accent-line accent-line-center" style={{ margin: "1rem auto 1.5rem" }} />
              <h2 className="section-title-modern">
                Mission, Vision &amp; <span>Values</span>
              </h2>
            </div>
          </div>

          <div className="row g-4">
            <div className="col-12 col-lg-4">
              <ValueCard
                icon="fas fa-bullseye"
                title="Our Mission"
                gradient="linear-gradient(135deg,#37514B,#00b8b8)"
                delay="0s"
                text="Improve health outcome for all."
              />
            </div>
            <div className="col-12 col-lg-4">
              <ValueCard
                icon="fas fa-eye"
                title="Our Vision"
                gradient="linear-gradient(135deg,#00b8b8,#4a6d65)"
                delay="0.15s"
                text="Transform healthcare delivery in rural, suburban and urban communities by building lasting partnerships and optimal use of innovation."
              />
            </div>
            <div className="col-12 col-lg-4">
              <ValueCard
                icon="fas fa-heart"
                title="Our Value"
                gradient="linear-gradient(135deg,#4a6d65,#37514B)"
                delay="0.3s"
                items={missionItems}
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── Services ── */}
      <section style={{ padding: "5rem 0 6rem", background: "linear-gradient(180deg,#f0f9f9 0%,#fff 100%)" }}>
        <div className="container">
          <div className="row justify-content-center" style={{ marginBottom: "3rem" }}>
            <div className="col-12 col-lg-7 text-center">
              <div className="section-tag">What We Do</div>
              <div className="accent-line accent-line-center" style={{ margin: "1rem auto 1.5rem" }} />
              <h2 className="section-title-modern">
                Our <span>Services</span>
              </h2>
            </div>
          </div>
          <div className="row g-4 justify-content-center">
            {[
              { icon: "fas fa-user-md", title: "2nd Medical Opinion", desc: "Independent expert opinions from board-certified specialists to help you understand your diagnosis.", link: "/home#services", label: "Get Opinion" },
              { icon: "fas fa-phone-alt", title: "24/7 Doctors on Call", desc: "Round-the-clock access to doctors via phone or video for urgent and non-urgent medical care.", link: "/call-center", label: "Call Now" },
            ].map((item, i) => (
              <div key={i} className="col-12 col-lg-4 col-md-6">
                <div
                  style={{
                    background: "#fff",
                    borderRadius: 20,
                    padding: "2.5rem 2rem",
                    boxShadow: "0 15px 50px rgba(0,0,0,0.08)",
                    border: "1px solid rgba(0,184,184,0.1)",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    transition: "all 0.4s ease",
                    position: "relative",
                    overflow: "hidden",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-10px)"; e.currentTarget.style.boxShadow = "0 30px 80px rgba(0,0,0,0.15)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 15px 50px rgba(0,0,0,0.08)"; }}
                >
                  <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 4, background: "linear-gradient(90deg,#37514B,#00b8b8)", borderRadius: "20px 20px 0 0" }} />
                  <div style={{ width: 60, height: 60, borderRadius: 16, background: "rgba(0,184,184,0.12)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.5rem", color: "#00b8b8", marginBottom: "1.5rem" }}>
                    <i className={item.icon} />
                  </div>
                  <h5 style={{ fontWeight: 700, color: "#1a2332", marginBottom: "1rem", fontSize: "1.05rem" }}>{item.title}</h5>
                  <p style={{ color: "#6b7280", lineHeight: 1.8, fontSize: "0.9rem", flexGrow: 1, marginBottom: "1.5rem" }}>{item.desc}</p>
                  <a href={item.link} className="btn-modern-outline" style={{ alignSelf: "flex-start", fontSize: "0.85rem", padding: "0.6rem 1.4rem" }}>
                    {item.label} <i className="fas fa-arrow-right" style={{ marginLeft: 6 }} />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA Banner ── */}
      <section
        style={{
          padding: "5rem 0",
          background: "linear-gradient(135deg,#253832 0%,#37514B 50%,#1a4a42 100%)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div style={{ position: "absolute", top: "50%", left: "50%", width: 700, height: 700, background: "rgba(0,184,184,0.07)", borderRadius: "50%", filter: "blur(80px)", transform: "translate(-50%,-50%)", pointerEvents: "none" }} />
        <div style={{ position: "absolute", inset: 0, backgroundImage: "radial-gradient(rgba(0,184,184,0.08) 1px,transparent 1px)", backgroundSize: "32px 32px", pointerEvents: "none" }} />
        <div className="container" style={{ position: "relative", zIndex: 1 }}>
          <div className="row justify-content-center text-center">
            <div className="col-12 col-lg-8">
              <h2 style={{ color: "#fff", fontWeight: 800, fontSize: "clamp(1.6rem,4vw,2.5rem)", marginBottom: "1.25rem", lineHeight: 1.3 }}>
                Ready to access quality healthcare?
              </h2>
              <p style={{ color: "rgba(255,255,255,0.75)", lineHeight: 1.8, marginBottom: "2.5rem", fontSize: "1.05rem" }}>
                Join thousands of Nigerians who have already experienced the ease of 24Telemed's healthcare services.
              </p>
              <div style={{ display: "flex", gap: "1rem", justifyContent: "center", flexWrap: "wrap" }}>
                <a
                  href="/contact"
                  style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "white", color: "#37514B", padding: "1rem 2rem", borderRadius: 50, fontWeight: 700, textDecoration: "none", boxShadow: "0 8px 25px rgba(0,0,0,0.2)", transition: "all 0.3s ease", fontSize: "0.95rem" }}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.boxShadow = "0 15px 40px rgba(0,0,0,0.3)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 8px 25px rgba(0,0,0,0.2)"; }}
                >
                  <i className="fas fa-envelope" /> Contact Us Today
                </a>
                <a
                  href="/call-center"
                  style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(255,255,255,0.12)", border: "2px solid rgba(255,255,255,0.3)", color: "#fff", padding: "1rem 2rem", borderRadius: 50, fontWeight: 600, textDecoration: "none", transition: "all 0.3s ease", fontSize: "0.95rem", backdropFilter: "blur(8px)" }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.2)"; e.currentTarget.style.transform = "translateY(-3px)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.12)"; e.currentTarget.style.transform = "translateY(0)"; }}
                >
                  <i className="fas fa-phone-alt" /> Call Our Doctors
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};
